import { VStack } from '@/components/gluestack/vstack';
import { Pressable, PressableStateCallbackType } from 'react-native';
import { Text } from '@/components/gluestack/text';
import { Divider } from '@/components/gluestack/divider';
import { View } from '@/components/gluestack/view';
import { ReactNode } from 'react';

export interface IActionItem<T> {
  key?: string | number;
  label: string | ReactNode;
  value: T;
}

export interface IActionListProps<T> {
  /**
   * List of options to display
   */
  options: IActionItem<T>[];
  
  /**
   * Callback when an option is selected
   */
  onSelect: (value: T) => void;
  
  /**
   * Class name for the container
   */
  className?: string;
  
  /**
   * Whether to show dividers between items
   */
  showDividers?: boolean;
  
  /**
   * Custom rendering for each item
   */
  renderItem?: (item: IActionItem<T>, index: number) => ReactNode;
}

export function ActionList<T>({
  options,
  onSelect,
  className = 'native:mr-4',
  showDividers = true,
  renderItem,
}: IActionListProps<T>) {
  return (
    <VStack className={className}>
      {options.map((option, index) => (
        <View key={option.key !== undefined ? option.key : index} className="w-full">
          {renderItem ? (
            renderItem(option, index)
          ) : (
            <Pressable onPress={() => onSelect(option.value)} className="rounded-md">
              {({ pressed, hovered }: PressableStateCallbackType & { hovered?: boolean }) => (
                <View
                  className={`rounded-md px-4 py-3 ${
                    pressed ? 'bg-neutral-200 dark:bg-neutral-600' : hovered ? 'bg-neutral-100 dark:bg-neutral-700' : ''
                  }`}>
                  {typeof option.label === 'string' ? (
                    <Text size="md" className={'font-medium'}>
                      {option.label}
                    </Text>
                  ) : (
                    option.label
                  )}
                </View>
              )}
            </Pressable>
          )}
          {showDividers && index < options.length - 1 && (
            <Divider orientation="horizontal" className="my-2 bg-neutral-200 dark:bg-neutral-600" />
          )}
        </View>
      ))}
    </VStack>
  );
}
